import React, { FC, useEffect, useRef, useState } from "react";
import DataTable, {
  Direction,
  ConditionalStyles,
} from "react-data-table-component";
import { generateGuid, isArabicCurrentLanguage } from "../../../utils";
import { DataTablePropsModel } from "../../../models";
import { DataTablePager } from "../../pagination/dataTablePager";
import {LoadingBox} from "../box/loadingBox";

interface HeaderWrapperProps {
  text: string | React.ReactNode;
}

// Header wrapper component
const HeaderWrapper: React.FC<HeaderWrapperProps> = ({ text }) => {
  if (React.isValidElement(text)) {
    return text; // Directly return ReactNode (e.g., checkbox)
  }
  return (
      <div className="whitespace-normal min-w-[150px] max-w-[300px] py-2">
        {text}
      </div>
  );
};

export interface SelectedRow<T> {
  allSelected: boolean;
  selectedCount: number;
  selectedRows: T[];
}

export const TableComponent: FC<DataTablePropsModel<any>> = ({
                                                               title,
                                                               columns = [],
                                                               data = [],
                                                               totalRows,
                                                               currentPage = 1,
                                                               pageSize,
                                                               paginationType = "none",
                                                               subHeaderComponent,
                                                               onPageSizeChange = () => {},
                                                               onCurrentPageChange = () => {},
                                                               onRowClicked = () => {},
                                                               onRowDoubleClicked = () => {},
                                                               onRowMouseEnter = () => {},
                                                               onRowMouseLeave = () => {},
                                                               conditionalRowStyles,
                                                               customStyles,
                                                               selectableRows = false,
                                                               onSelectedRowChange,
                                                               progressComponent,
                                                               externalLoading
                                                             }) => {
  //#region state
  // Create a ref that persists across rerenders to track first load
  const initialRenderRef = useRef(true);

  // Only set loading to true on the very first render
  const [loading, setLoading] = useState(initialRenderRef.current);
  const [activeRowId, setActiveRowId] = useState<any>(null);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [internalCurrentPage, setInternalCurrentPage] = useState(currentPage);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const isPaginationRequired = paginationType !== "none";

  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Memoized columns to avoid unnecessary re-renders
  const wrappedColumns = React.useMemo(
      () =>
          columns.map((column) => ({
            ...column,
            name: <HeaderWrapper text={column.name} />,
          })),
      [columns]
  );
  //#endregion
  //#region useEffect
  // Handle initial data load only
  useEffect(() => {
    // Only run this effect on the very first render
    if (initialRenderRef.current) {
      initialRenderRef.current = false; // Mark that we've handled the initial render

      if (data && data.length > 0) {
        // Data is available on first render, show brief loading state
        const delay = Math.min(300 + data.length, 1000); // Shorter initial delay

        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
        }

        loadingTimerRef.current = setTimeout(() => {
          setLoading(false);
        }, delay);
      } else {
        // Empty data on first render, don't show loader
        setLoading(false);
      }
    }

    // Cleanup
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  // Effect to process data and update displayed data without affecting loading state
  useEffect(() => {
    // Add unique IDs to data for tracking
    const addUniqueIdToData = (dataToProcess: any[]) => {
      return dataToProcess.map((row: any) => ({
        ...row,
        uniqueId: row.uniqueId || generateGuid(),
      }));
    };

    // Process data based on pagination type
    let processedData: any[] = [];
    if (paginationType === "client" && data.length > 0) {
      const startIdx = (internalCurrentPage - 1) * internalPageSize;
      const endIdx = startIdx + internalPageSize;
      processedData = addUniqueIdToData(data.slice(startIdx, endIdx));
    } else {
      processedData = addUniqueIdToData(data);
    }

    // Update displayed data with processed version
    setDisplayedData([...processedData]);
  }, [internalCurrentPage, internalPageSize, paginationType, data]);
  //#endregion
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  const isPaginationServer: boolean = paginationType === "server";
  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "/",
  };
  const rowsPerPage = [5, 10, 25, 50, 100, 200, 300, 500];

  // Use external loading state if provided, otherwise use internal state
  const isLoading = externalLoading !== undefined ? externalLoading : loading;
  //#endregion
  //#region functions
  // Conditional styles for rows (hover effect)
  const hoverStyle: ConditionalStyles<any>[] = [
    {
      when: (row: any) => {
        const rowId = row.uniqueId;
        return rowId === activeRowId;
      },
      style: {
        backgroundColor: "#e0f7fa",
      },
    },
  ];

  // Row event handlers
  const handleRowMouseEnter = (row: any, event: React.MouseEvent) => {
    const rowId = row.uniqueId;
    setActiveRowId(rowId);
    if (onRowMouseEnter) {
      onRowMouseEnter(row, event);
    }
  };

  const handleRowMouseLeave = (row: any, event: React.MouseEvent) => {
    setActiveRowId(null);
    if (onRowMouseLeave) {
      onRowMouseLeave(row, event);
    }
  };

  const handleRowClick = (row: any, event: React.MouseEvent) => {
    const rowId = row.uniqueId;
    setActiveRowId(rowId);
    if (onRowClicked) {
      onRowClicked(row, event);
    }
  };

  const handleRowDoubleClick = (row: any, event: React.MouseEvent) => {
    const rowId = row.uniqueId;
    setActiveRowId(rowId);
    if (onRowDoubleClicked) {
      onRowDoubleClicked(row, event);
    }
  };

  // Pagination handlers
  const handleChangePage = (newPage: number) => {
    if (internalCurrentPage !== newPage) {
      setInternalCurrentPage(newPage);

      if (paginationType === "server") {
        // Only set loading for server-side pagination
        setLoading(true);
        onCurrentPageChange(newPage);
      }
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (internalPageSize !== newPageSize) {
      setInternalPageSize(newPageSize);
      setInternalCurrentPage(1);

      if (paginationType === "server") {
        // Only set loading for server-side pagination
        setLoading(true);
        onPageSizeChange(newPageSize);
      }
    }
  };
  //#endregion
  //#region custom loading component
  const defaultProgressComponent = (
      <>
        <LoadingBox/>
        <div className="p-4 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          <span className="ml-2">Loading data...</span>
        </div>
      </>
  );

  const finalProgressComponent = progressComponent || defaultProgressComponent;
  //#endregion
  //#region html
  return (
      <>
        <DataTable
            key={`${internalCurrentPage}-${internalPageSize}-${data.length}`} // Include data.length to force re-render on data changes
            title={title}
            columns={wrappedColumns}
            data={displayedData}
            paginationPerPage={internalPageSize}
            // progressComponent={<>loading........</>}
            progressComponent={finalProgressComponent}
            progressPending={isLoading}
            pagination={isPaginationRequired}
            paginationServer={isPaginationServer}
            paginationTotalRows={totalRows}
            paginationDefaultPage={internalCurrentPage}
            direction={isArabic ? Direction.RTL : Direction.LTR}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handlePageSizeChange}
            onRowClicked={handleRowClick}
            onRowDoubleClicked={handleRowDoubleClick}
            onRowMouseEnter={handleRowMouseEnter}
            onRowMouseLeave={handleRowMouseLeave}
            selectableRowsHighlight
            selectableRowsSingle
            selectableRows={selectableRows}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={rowsPerPage}
            onSelectedRowsChange={onSelectedRowChange}
            striped
            responsive
            fixedHeader
            fixedHeaderScrollHeight="60vh"
            subHeaderComponent={subHeaderComponent}
            conditionalRowStyles={
                [...(conditionalRowStyles || []), ...hoverStyle] || []
            }
            noDataComponent={<div></div>}
            paginationComponent={
              isPaginationRequired
                  ? () => (
                      <DataTablePager
                          currentPage={internalCurrentPage}
                          rowsPerPage={internalPageSize}
                          totalRows={totalRows}
                          rowsPerPageOptions={rowsPerPage}
                          onPageChange={(page) => {
                            handleChangePage(page);
                          }}
                          onRowsPerPageChange={handlePageSizeChange}
                      />
                  )
                  : undefined
            }
            customStyles={customStyles}
        />
      </>
  );
  //#endregion
};




//#region original implement


//
// //https://codesandbox.io/s/ccyuu?file=/src/movies.js  -->basic
// //https://codesandbox.io/s/xmdju?file=/src/index.js:1950-1966  -->server side pagination
// //https://codesandbox.io/s/fz295?file=/src/DataTable.js:1750-1763 -->filter
// //https://react-data-table-component.netlify.app/
// import React, { FC, useEffect, useState } from "react";
// import DataTable, {
//     Direction,
//     ConditionalStyles,
// } from "react-data-table-component";
// import { generateGuid, isArabicCurrentLanguage } from "../../../utils";
// import { DataTablePropsModel } from "../../../models";
// import { DataTablePager } from "../../pagination/dataTablePager";
//
// //type PaginationType = "none" | "client" | "server";
//
// interface HeaderWrapperProps {
//     text: string | React.ReactNode;
// }
//
// // Header wrapper component
// const HeaderWrapper1: React.FC<HeaderWrapperProps> = ({ text }) => (
//     <div className="whitespace-normal min-w-[150px] max-w-[300px] py-2">
//         {text}
//     </div>
// );
// const HeaderWrapper: React.FC<HeaderWrapperProps> = ({ text }) => {
//     if (React.isValidElement(text)) {
//         return text; // Directly return ReactNode (e.g., checkbox)
//     }
//     return (
//         <div className="whitespace-normal min-w-[150px] max-w-[300px] py-2">
//             {text}
//         </div>
//     );
// };
//
// export interface SelectedRow<T> {
//     allSelected: boolean;
//     selectedCount: number;
//     selectedRows: T[];
// }
//
// export const TableComponent: FC<DataTablePropsModel<any>> = ({
//                                                                  title,
//                                                                  columns = [],
//                                                                  data = [],
//                                                                  totalRows,
//                                                                  currentPage = 1,
//                                                                  pageSize,
//                                                                  paginationType = "none",
//                                                                  subHeaderComponent,
//                                                                  onPageSizeChange = () => {},
//                                                                  onCurrentPageChange = () => {},
//                                                                  onRowClicked = () => {},
//                                                                  onRowDoubleClicked = () => {},
//                                                                  onRowMouseEnter = () => {},
//                                                                  onRowMouseLeave = () => {},
//                                                                  conditionalRowStyles,
//                                                                  customStyles,
//                                                                  selectableRows = false,
//                                                                  onSelectedRowChange,
//                                                              }) => {
//     //#region state
//     const [activeRowId, setActiveRowId] = useState<any>(null);
//     const [internalPageSize, setInternalPageSize] = useState(pageSize); // Internal state for page size
//     const [internalCurrentPage, setInternalCurrentPage] = useState(currentPage); // Internal state for current page
//     const [displayedData, setDisplayedData] = useState(data); // Data to be displayed in client pagination
//     const isPaginationRequired = paginationType !== "none"; //&& totalRows > pageSize;
//     // Wrap column headers with HeaderWrapper
//     const wrappedColumns1 = React.useMemo(
//         () =>
//             columns.map((column) => ({
//                 ...column,
//                 //header: column.header || column.name,
//                 //name: <HeaderWrapper text={column.name} />
//                 name: (
//                     <HeaderWrapper
//                         text={typeof column.name === "string" ? column.name : ""}
//                     />
//                 ),
//             })),
//         [columns]
//     );
//     const wrappedColumns = React.useMemo(
//         () =>
//             columns.map((column) => ({
//                 ...column,
//                 name: <HeaderWrapper text={column.name} />,
//             })),
//         [columns]
//     );
//     // const [hoveredRowId, setHoveredRowId] = useState<any>(null);
//     // const [clickedRowId, setClickedRowId] = useState<any>(null);
//     //#endregion
//     //#region effects
//     // Effect to update displayed data for client-side pagination
//     useEffect(() => {
//         const addUniqueIdToData = (data: any) => {
//             return data.map((row: any) => ({
//                 ...row,
//                 uniqueId: row.uniqueId || generateGuid(), // Preserve existing uniqueId or generate a new one
//             }));
//         };
//
//         if (paginationType === "client") {
//             const startIdx = (internalCurrentPage - 1) * internalPageSize;
//             const endIdx = startIdx + internalPageSize;
//             const newDisplayedData = addUniqueIdToData(data.slice(startIdx, endIdx));
//             setDisplayedData([...newDisplayedData]); // Creating a new reference
//         } else {
//             const newDisplayedData = addUniqueIdToData(data);
//             setDisplayedData(newDisplayedData);
//         }
//     }, [internalCurrentPage, internalPageSize, paginationType, data]);
//     //#endregion
//     //#region variables
//     const isArabic = isArabicCurrentLanguage();
//     let isPaginationServer: boolean = paginationType === "server";
//     const paginationComponentOptions = {
//         // selectAllRowsItem: true,
//         // selectAllRowsItemText: t("selectAllRowsFromTable"),
//         // rowsPerPageText: t("rowsPerPageText"),
//         //rangeSeparatorText: t("rangeSeparatorText"),
//         rowsPerPageText: "",
//         rangeSeparatorText: "/", //`Page ${currentPage} of ${10}`, // Show Page X of Y
//     };
//     const rowsPerPage = [5, 10, 25, 50, 100, 200, 300, 500];
//     //const totalPages = Math.ceil(totalRows / pageSize);
//     //#endregion
//     //#region function
//     const hoverStyle: ConditionalStyles<any>[] = [
//         {
//             when: (row: any) => {
//                 const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//                 return rowId === activeRowId;
//             },
//             style: {
//                 backgroundColor: "#e0f7fa", // Change this to your desired hover/click color
//             },
//         },
//         // {
//         //     when: (row: any) => row.id === hoveredRowId,
//         //     style: {
//         //         backgroundColor: '#f1f1f1', // Change this to your desired hover color
//         //     },
//         // },
//         // {
//         //     when: (row: any) => row.id === clickedRowId,
//         //     style: {
//         //         backgroundColor: '#d1d1d1', // Change this to your desired click color
//         //     },
//         // },
//     ];
//     const handleRowMouseEnter = (row: any, event: React.MouseEvent) => {
//         //setHoveredRowId(row.id);
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowMouseEnter) {
//             onRowMouseEnter(row, event);
//         }
//     };
//     const handleRowMouseLeave = (row: any, event: React.MouseEvent) => {
//         //setHoveredRowId(null);
//         setActiveRowId(null);
//         if (onRowMouseLeave) {
//             onRowMouseLeave(row, event);
//         }
//     };
//     const handleRowClick = (row: any, event: React.MouseEvent) => {
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowClicked) {
//             onRowClicked(row, event);
//         }
//     };
//     const handleRowDoubleClick = (row: any, event: React.MouseEvent) => {
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowDoubleClicked) {
//             onRowDoubleClicked(row, event);
//         }
//     };
//     const handleChangePage = (newPage: number) => {
//         if (internalCurrentPage !== newPage) {
//             setInternalCurrentPage(newPage);
//             if (paginationType === "server") {
//                 onCurrentPageChange(newPage); // Delegate to external handler for server-side
//             }
//         }
//     };
//     const handlePageSizeChange = (newPageSize: number) => {
//         if (internalPageSize !== newPageSize) {
//             setInternalPageSize(newPageSize); // Handle client-side page size change
//             setInternalCurrentPage(1);
//             if (paginationType === "server") {
//                 onPageSizeChange(newPageSize); // Delegate to external handler for server-side
//             }
//         }
//     };
//     // const CustomPagination = (): any => {
//     //   return (
//     //     <>
//     //       <Pager
//     //         currentPage={currentPage}
//     //         pageSize={pageSize}
//     //         rowsCount={totalRows}
//     //         onCurrentPageChange={onCurrentPageChange}
//     //         onPageSizeChange={onPageSizeChange}
//     //       />
//     //     </>
//     //   );
//     // };
//     //#endregion
//     //#region html
//     return (
//         <>
//             <DataTable
//                 key={internalCurrentPage + "-" + internalPageSize} // Force re-render by changing the key
//                 title={title}
//                 //columns={columns}
//                 columns={wrappedColumns}
//                 data={displayedData} //data
//                 paginationPerPage={internalPageSize} //pageSize
//                 progressComponent={<>loading........</>}
//                 //progressPending={loading} // to be checked
//                 pagination={isPaginationRequired}
//                 paginationServer={isPaginationServer}
//                 paginationTotalRows={totalRows}
//                 paginationDefaultPage={internalCurrentPage} //currentPage
//                 direction={isArabic ? Direction.RTL : Direction.LTR}
//                 // onChangePage={(e) => {onCurrentPageChange(e);}}
//                 // //@ts-ignore
//                 // onChangeRowsPerPage={(e) => {onPageSizeChange(e);}}
//                 onChangePage={handleChangePage} // Call the appropriate handler based on pagination type
//                 onChangeRowsPerPage={handlePageSizeChange} // Handle page size changes
//                 onRowClicked={handleRowClick}
//                 onRowDoubleClicked={handleRowDoubleClick}
//                 onRowMouseEnter={handleRowMouseEnter}
//                 onRowMouseLeave={handleRowMouseLeave}
//                 selectableRowsHighlight
//                 selectableRowsSingle
//                 selectableRows={selectableRows}
//                 paginationComponentOptions={paginationComponentOptions}
//                 paginationRowsPerPageOptions={rowsPerPage}
//                 onSelectedRowsChange={onSelectedRowChange}
//                 striped
//                 responsive
//                 fixedHeader
//                 fixedHeaderScrollHeight="60vh"
//                 //subHeader
//                 subHeaderComponent={subHeaderComponent}
//                 conditionalRowStyles={
//                     [...(conditionalRowStyles || []), ...hoverStyle] || []
//                 }
//                 //conditionalRowStyles={conditionalRowStyles || []}
//                 noDataComponent={<div></div>}
//                 //customStyles={{backgroundColor: 'yellow'}}
//                 //paginationComponent={isCustomPagination ? CustomPagination : undefined}
//                 paginationComponent={
//                     isPaginationRequired
//                         ? () => (
//                             <DataTablePager
//                                 currentPage={internalCurrentPage}
//                                 rowsPerPage={internalPageSize}
//                                 totalRows={totalRows}
//                                 rowsPerPageOptions={rowsPerPage}
//                                 onPageChange={(page) => {
//                                     handleChangePage(page);
//                                 }}
//                                 onRowsPerPageChange={handlePageSizeChange}
//                             />
//                         )
//                         : undefined
//                 }
//                 customStyles={customStyles}
//             />
//         </>
//     );
//     //#endregion
// };








//
// //https://codesandbox.io/s/ccyuu?file=/src/movies.js  -->basic
// //https://codesandbox.io/s/xmdju?file=/src/index.js:1950-1966  -->server side pagination
// //https://codesandbox.io/s/fz295?file=/src/DataTable.js:1750-1763 -->filter
// //https://react-data-table-component.netlify.app/
// import React, { FC, useEffect, useState } from "react";
// import DataTable, { Direction,ConditionalStyles  } from "react-data-table-component";
// import { generateGuid, isArabicCurrentLanguage } from "../../../utils";
// import { DataTablePropsModel } from "../../../models";
// import { DataTablePager } from "../../pagination/dataTablePager";
//
// //type PaginationType = "none" | "client" | "server";
//
// interface HeaderWrapperProps {
//     text: string | React.ReactNode;
// }
//
// // Header wrapper component
// const HeaderWrapper1: React.FC<HeaderWrapperProps> = ({ text }) => (
//     <div className="whitespace-normal min-w-[150px] max-w-[300px] py-2">
//         {text}
//     </div>
// );
// const HeaderWrapper: React.FC<HeaderWrapperProps> = ({ text }) => {
//     if (React.isValidElement(text)) {
//         return text; // Directly return ReactNode (e.g., checkbox)
//     }
//     return <div className="whitespace-normal min-w-[150px] max-w-[300px] py-2">{text}</div>;
// };
//
// export interface SelectedRow<T> {
//     allSelected: boolean;
//     selectedCount: number;
//     selectedRows: T[];
// }
//
// export const TableComponent: FC<DataTablePropsModel<any>> = ({
//                                                                  title,
//                                                                  columns = [],
//                                                                  data = [],
//                                                                  totalRows,
//                                                                  currentPage = 1,
//                                                                  pageSize,
//                                                                  paginationType = "none",
//                                                                  subHeaderComponent,
//                                                                  onPageSizeChange = () => {},
//                                                                  onCurrentPageChange = () => {},
//                                                                  onRowClicked = () => {},
//                                                                  onRowDoubleClicked = () => {},
//                                                                  onRowMouseEnter = () => {},
//                                                                  onRowMouseLeave = () => {},
//                                                                  conditionalRowStyles,
//                                                                  selectableRows = false,
//                                                                  onSelectedRowChange,
//                                                              }) => {
//     //#region state
//     const [activeRowId, setActiveRowId] = useState<any>(null);
//     const [internalPageSize, setInternalPageSize] = useState(pageSize); // Internal state for page size
//     const [internalCurrentPage, setInternalCurrentPage] = useState(currentPage); // Internal state for current page
//     const [displayedData, setDisplayedData] = useState(data); // Data to be displayed in client pagination
//     const isPaginationRequired = paginationType !== "none"; //&& totalRows > pageSize;
//     // Wrap column headers with HeaderWrapper
//     const wrappedColumns1 = React.useMemo(() =>
//             columns.map(column => ({
//                 ...column,
//                 //header: column.header || column.name,
//                 //name: <HeaderWrapper text={column.name} />
//                 name: <HeaderWrapper text={typeof column.name === 'string' ? column.name : ''} />
//             }))
//         , [columns]);
//     const wrappedColumns = React.useMemo(
//         () =>
//             columns.map(column => ({
//                 ...column,
//                 name: <HeaderWrapper text={column.name} />,
//             })),
//         [columns]
//     );
//     // const [hoveredRowId, setHoveredRowId] = useState<any>(null);
//     // const [clickedRowId, setClickedRowId] = useState<any>(null);
//     //#endregion
//     //#region effects
//     // Effect to update displayed data for client-side pagination
//     useEffect(() => {
//         const addUniqueIdToData = (data: any) => {
//             return data.map((row: any) => ({
//                 ...row,
//                 uniqueId: row.uniqueId || generateGuid(), // Preserve existing uniqueId or generate a new one
//             }));
//         };
//
//         if (paginationType === "client") {
//             const startIdx = (internalCurrentPage - 1) * internalPageSize;
//             const endIdx = startIdx + internalPageSize;
//             const newDisplayedData = addUniqueIdToData(data.slice(startIdx, endIdx));
//             setDisplayedData([...newDisplayedData]); // Creating a new reference
//         } else {
//             const newDisplayedData = addUniqueIdToData(data);
//             setDisplayedData(newDisplayedData);
//         }
//     }, [internalCurrentPage, internalPageSize, paginationType, data]);
//
//     //#endregion
//     //#region variables
//     const isArabic = isArabicCurrentLanguage();
//     let isPaginationServer: boolean = paginationType === "server";
//     const paginationComponentOptions = {
//         // selectAllRowsItem: true,
//         // selectAllRowsItemText: t("selectAllRowsFromTable"),
//         // rowsPerPageText: t("rowsPerPageText"),
//         //rangeSeparatorText: t("rangeSeparatorText"),
//         rowsPerPageText: "",
//         rangeSeparatorText: "/", //`Page ${currentPage} of ${10}`, // Show Page X of Y
//     };
//     const rowsPerPage = [5, 10, 25, 50, 100, 200, 300, 500];
//     //const totalPages = Math.ceil(totalRows / pageSize);
//     //#endregion
//     //#region function
//     const hoverStyle: ConditionalStyles<any>[] = [
//         {
//             when: (row: any) => {
//                 const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//                 return rowId === activeRowId;
//             },
//             style: {
//                 backgroundColor: "#e0f7fa", // Change this to your desired hover/click color
//             },
//         },
//         // {
//         //     when: (row: any) => row.id === hoveredRowId,
//         //     style: {
//         //         backgroundColor: '#f1f1f1', // Change this to your desired hover color
//         //     },
//         // },
//         // {
//         //     when: (row: any) => row.id === clickedRowId,
//         //     style: {
//         //         backgroundColor: '#d1d1d1', // Change this to your desired click color
//         //     },
//         // },
//     ];
//     const handleRowMouseEnter = (row: any, event: React.MouseEvent) => {
//         //setHoveredRowId(row.id);
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowMouseEnter) {
//             onRowMouseEnter(row, event);
//         }
//     };
//     const handleRowMouseLeave = (row: any, event: React.MouseEvent) => {
//         //setHoveredRowId(null);
//         setActiveRowId(null);
//         if (onRowMouseLeave) {
//             onRowMouseLeave(row, event);
//         }
//     };
//     const handleRowClick = (row: any, event: React.MouseEvent) => {
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowClicked) {
//             onRowClicked(row, event);
//         }
//     };
//     const handleRowDoubleClick = (row: any, event: React.MouseEvent) => {
//         const rowId = row.uniqueId; // row.id || row.ID || row.rowKey;
//         setActiveRowId(rowId);
//         if (onRowDoubleClicked) {
//             onRowDoubleClicked(row, event);
//         }
//     };
//     const handleChangePage = (newPage: number) => {
//         if (internalCurrentPage !== newPage) {
//             setInternalCurrentPage(newPage);
//             if (paginationType === "server") {
//                 onCurrentPageChange(newPage); // Delegate to external handler for server-side
//             }
//         }
//     };
//     const handlePageSizeChange = (newPageSize: number) => {
//         if (internalPageSize !== newPageSize) {
//             setInternalPageSize(newPageSize); // Handle client-side page size change
//             setInternalCurrentPage(1);
//             if (paginationType === "server") {
//                 onPageSizeChange(newPageSize); // Delegate to external handler for server-side
//             }
//         }
//     };
//     // const CustomPagination = (): any => {
//     //   return (
//     //     <>
//     //       <Pager
//     //         currentPage={currentPage}
//     //         pageSize={pageSize}
//     //         rowsCount={totalRows}
//     //         onCurrentPageChange={onCurrentPageChange}
//     //         onPageSizeChange={onPageSizeChange}
//     //       />
//     //     </>
//     //   );
//     // };
//     //#endregion
//     //#region html
//     return (
//         <>
//             <DataTable
//                 key={internalCurrentPage + "-" + internalPageSize} // Force re-render by changing the key
//                 title={title}
//                 //columns={columns}
//                 columns={wrappedColumns}
//                 data={displayedData} //data
//                 paginationPerPage={internalPageSize} //pageSize
//                 progressComponent={<>loading........</>}
//                 //progressPending={loading} // to be checked
//                 pagination={isPaginationRequired}
//                 paginationServer={isPaginationServer}
//                 paginationTotalRows={totalRows}
//                 paginationDefaultPage={internalCurrentPage} //currentPage
//                 direction={isArabic ? Direction.RTL : Direction.LTR}
//                 // onChangePage={(e) => {onCurrentPageChange(e);}}
//                 // //@ts-ignore
//                 // onChangeRowsPerPage={(e) => {onPageSizeChange(e);}}
//                 onChangePage={handleChangePage} // Call the appropriate handler based on pagination type
//                 onChangeRowsPerPage={handlePageSizeChange} // Handle page size changes
//                 onRowClicked={handleRowClick}
//                 onRowDoubleClicked={handleRowDoubleClick}
//                 onRowMouseEnter={handleRowMouseEnter}
//                 onRowMouseLeave={handleRowMouseLeave}
//                 selectableRowsHighlight
//                 selectableRowsSingle
//                 selectableRows={selectableRows}
//                 paginationComponentOptions={paginationComponentOptions}
//                 paginationRowsPerPageOptions={rowsPerPage}
//                 onSelectedRowsChange={onSelectedRowChange}
//                 striped
//                 responsive
//                 fixedHeader
//                 fixedHeaderScrollHeight="60vh"
//                 //subHeader
//                 subHeaderComponent={subHeaderComponent}
//                 conditionalRowStyles={
//                     [...(conditionalRowStyles || []), ...hoverStyle] || []
//                 }
//                 //conditionalRowStyles={conditionalRowStyles || []}
//                 noDataComponent={<div></div>}
//                 //customStyles={{backgroundColor: 'yellow'}}
//                 //paginationComponent={isCustomPagination ? CustomPagination : undefined}
//                 paginationComponent={
//                     isPaginationRequired
//                         ? () => (
//                             <DataTablePager
//                                 currentPage={internalCurrentPage}
//                                 rowsPerPage={internalPageSize}
//                                 totalRows={totalRows}
//                                 rowsPerPageOptions={rowsPerPage}
//                                 onPageChange={(page) => {
//                                     handleChangePage(page);
//                                 }}
//                                 onRowsPerPageChange={handlePageSizeChange}
//                             />
//                         )
//                         : undefined
//                 }
//             />
//         </>
//     );
//     //#endregion
// };



//#endregion